import type { ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';

import type { TButtonElement } from '@/buttonElement';
import type { TImageElement } from '@/imageElement';
import type { SerializableNode, SerializerProps } from '@/serializer';
import { updateText } from '@/utils/post/utils';

import { getStructureElement } from '../../serializer/getStructureElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import { YoutubeImageItem } from './YoutubeImageItem';

interface PodcastSerializerProps extends SerializerProps {
  episodeTitle: string;
  episodeDescription?: string;
  episodePublished: string;
  episodeThumbnailUrl: string;
  episodeThumbnailSrcSet?: string;
  episodeThumbnailSizes?: string;
  episodeUrl?: string;
  node: JSONContent & SerializableNode;
  isLive?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  showPlayButton?: boolean;
}

const formatEpisodeDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    episodeTitle,
    episodeDescription,
    episodePublished,
    episodeThumbnailUrl,
    episodeThumbnailSrcSet,
    episodeThumbnailSizes,
    episodeUrl,
    isLive,
    onClick,
    showPlayButton,
  }: PodcastSerializerProps,
  path = [0],
): ReactNode => {
  const key = createKey([node, path]);
  // The components short-circuited below never reach getStructureElement.
  const nodeId = node?.attrs?.id;
  const dataAttributes = {
    'data-json-path': path.join(','),
    ...(nodeId ? { 'data-node-id': String(nodeId) } : {}),
  };

  const isImage = node?.type === 'image';
  const isButton = node?.type === 'button';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';

  // Swap out images with merge tags
  if (isImage && node.attrs?.mergeTag && node.attrs.mergeTag === 'podcast.episode.image') {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        alt: episodeTitle,
        src: episodeThumbnailUrl,
      },
    } as JSONContent & SerializableNode;

    if (showPlayButton || episodeThumbnailSrcSet) {
      return (
        <YoutubeImageItem
          node={node as TImageElement}
          onClick={onClick}
          showPlayButton={showPlayButton}
          srcSet={episodeThumbnailSrcSet}
          sizes={episodeThumbnailSizes}
          attributes={dataAttributes}
        />
      );
    }
  }

  // Swap out headings and paragraphs with merge tags
  const mergeTag = node.attrs?.mergeTag;
  if ((isParagraph || isHeading) && mergeTag) {
    if (mergeTag === 'podcast.episode.title') {
      node = updateText(node, episodeTitle) as JSONContent & SerializableNode;
    }
    if (mergeTag === 'podcast.episode.description') {
      node = updateText(node, episodeDescription || '') as JSONContent & SerializableNode;
    }
    if (mergeTag === 'podcast.episode.publish_date') {
      node = updateText(node, formatEpisodeDate(episodePublished)) as JSONContent & SerializableNode;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  if (isButton) {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        renderAsDiv: true,
        ...(mergeTag === 'podcast.episode.page_button' &&
          episodeUrl && {
            open_in_new_tab: true,
            onClick: 'link',
            url: episodeUrl,
            renderAsDiv: false,
          }),
      },
    } as TButtonElement;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          episodeTitle,
          episodeDescription,
          episodePublished,
          episodeThumbnailUrl,
          episodeThumbnailSrcSet,
          episodeThumbnailSizes,
          episodeUrl,
          isLive,
          onClick,
          showPlayButton,
        },
        [...path, i],
      ),
    ) || [];

  return getStructureElement({
    node: node as SerializableNode,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
    jsonPath: path,
  });
};

export function PodcastSerializer(props: PodcastSerializerProps) {
  return <>{serializeNode({ ...props })}</>;
}
